import { useState, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";

import Input from "components/v2/Input/Input";
import Radio, { RadioOption } from "components/v2/Radio/Radio";
import Button from "components/v2/Button/Button";
import Divider from "components/v2/Divider/Divider";
import { generateFedexPin, validateFedexAddress } from "api";
import errorToast from "helpers/errorToast";
import { useCreateParcelAccountContext } from "../CreateParcelAccountState";

export type CreateParcelAccountVerificationItemProps = {
  radioOptions: RadioOption[];
  description: string;
  label: string;
  placeholder: string;
  maxRetriesReached: boolean;
  setMaxRetriesReached: React.Dispatch<React.SetStateAction<boolean>>;
};

interface GenerateFedexPinPayload {
  account_number: string | undefined;
  mfa_option: string;
}

function CreateParcelAccountVerificationItem(props: CreateParcelAccountVerificationItemProps) {
  const { radioOptions, description, label, placeholder, maxRetriesReached, setMaxRetriesReached } = props;
  const [codeSent, setCodeSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const { state } = useCreateParcelAccountContext();
  const { addressFormData } = state;
  const { control, watch } = useFormContext();
  const mfaOption = watch("mfa_option");
  const intl = useIntl();
  const hasRetriedSessionRef = useRef(false);

  // Optimized timer effect - only runs when timer starts/stops
  useEffect(() => {
    if (resendTimer > 0 && !intervalRef.current) {
      // Start countdown only if not already running
      intervalRef.current = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            // Clear interval when countdown reaches 0
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (resendTimer === 0 && intervalRef.current) {
      // Clear interval if timer is manually reset to 0
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [resendTimer]);

  // Cleanup interval on unmount
  useEffect(
    () => () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    },
    [],
  );

  const buildGeneratePinPayload = (): GenerateFedexPinPayload => ({
    account_number: addressFormData?.account_details?.account_number,
    mfa_option: mfaOption,
  });

  const sendGeneratePin = () => {
    generatePin(buildGeneratePinPayload());
  };

  const handleError = (error: any) => {
    error.response.data = error.response.data?.non_field_errors || error.response.data;
    errorToast(error);
  };

  const { mutate: generatePin, isLoading: isGeneratingPin } = useMutation({
    mutationFn: (payload: GenerateFedexPinPayload) => generateFedexPin(payload),
    onSuccess: () => {
      hasRetriedSessionRef.current = false;
      toast.success(
        intl.formatMessage({
          id: "settings.parcelAccounts.createAccount.mfa.codeSent",
          defaultMessage: "Verification code sent successfully",
        }),
      );
      setCodeSent(true);
      setResendTimer(60); // 60 seconds countdown
    },
    onError: async (error: any) => {
      const errorMessage = error?.response?.data?.non_field_errors?.[0];
      const shouldRetrySession = [
        "FedEx Error: Please validate address first.",
        "FedEx Error: Session has expired. Please validate address again.",
      ].includes(errorMessage);
      const isMaxRetriesReached = errorMessage === "FedEx Error: Max retry exceeded for PIN Generation.";

      if (isMaxRetriesReached) {
        setMaxRetriesReached(true);
      }

      if (shouldRetrySession && !hasRetriedSessionRef.current && addressFormData) {
        hasRetriedSessionRef.current = true;
        try {
          const payload = {
            ...addressFormData,
            account_details: {
              ...addressFormData.account_details,
              available_to_locations: addressFormData.account_details?.available_to_locations || [],
            },
          };
          await validateFedexAddress(payload);
          sendGeneratePin();
          return;
        } catch (retryError: any) {
          handleError(retryError);
          return;
        }
      }
      handleError(error);
    },
  });

  const handleSendCode = () => {
    if (resendTimer > 0) return; // Prevent action if timer is active
    sendGeneratePin();
  };

  let buttonLabel = intl.formatMessage({
    id: "settings.parcelAccounts.createAccount.mfa.sendCode",
    defaultMessage: "Send code",
  });
  if (isGeneratingPin) {
    buttonLabel = intl.formatMessage({
      id: "settings.parcelAccounts.createAccount.mfa.sendingCode",
      defaultMessage: "Sending...",
    });
  } else if (resendTimer > 0) {
    buttonLabel = intl.formatMessage(
      {
        id: "settings.parcelAccounts.createAccount.mfa.resendCodeIn",
        defaultMessage: "Resend code in {seconds}s",
      },
      { seconds: resendTimer },
    );
  } else if (codeSent) {
    buttonLabel = intl.formatMessage({
      id: "settings.parcelAccounts.createAccount.mfa.resendCode",
      defaultMessage: "Resend code",
    });
  }

  return (
    <div className="mt-2xl create-parcel-account__configuration-radio">
      <Controller
        name="mfa_option"
        control={control}
        render={({ field }) => <Radio options={radioOptions} value={field.value} size="md" onChange={field.onChange} />}
      />
      <div className="create-parcel-account__verification-content">
        <div className="text-md text-tertiary">{description}</div>
        {mfaOption === radioOptions[0].value && (
          <>
            <div className="d-flex mt-lg">
              <div className="mr-lg flex-1 create-parcel-account__verification-content-input-container">
                <Input label={label} disabled placeholder={placeholder} />
              </div>
              <div className="align-self-end">
                <Button
                  label={buttonLabel}
                  variant="secondary-color"
                  onClick={handleSendCode}
                  disabled={isGeneratingPin || resendTimer > 0 || maxRetriesReached}
                />
              </div>
            </div>
            <div className="mt-lg create-parcel-account__verification-content-input-container">
              <Input
                label={intl.formatMessage({
                  id: "settings.parcelAccounts.createAccount.mfa.enterCode",
                  defaultMessage: "Enter code",
                })}
                name="pin"
              />
            </div>
          </>
        )}
        <Divider className="mt-2xl" />
      </div>
    </div>
  );
}

export default CreateParcelAccountVerificationItem;
